import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { requestJSON } from '../core/http';
import { emitObservation } from '../core/observability';
import { useSessionStore } from './session-store';
import { createLogger } from '@shared/utils/logger';
import { OrderPersistence } from '../modules/order-persistence';

const log = createLogger('OrdersStore');

type SessionSummary = {
  status: string;
  subtotal: number;
  tax_amount: number;
  tip_amount: number;
  total_amount: number;
};

export type OrderItem = {
  id: string | number;
  menu_item_id?: string | number;
  name: string;
  price?: number;
  unit_price?: number;
  total_price?: number;
  quantity: number;
  extras?: string[];
  modifiers?: Array<unknown>;
  special_instructions?: string;
  cancelled_at?: string | null;
};

export type ClientOrder = {
  id: string;
  display_id?: string;
  display_order_id?: string;
  workflow_status?: string;
  status?: string;
  subtotal?: number;
  tax_amount?: number;
  tip_amount?: number;
  total_amount: number;
  items: OrderItem[];
  created_at?: string;
  /** Internal reconciliation metadata — never sent to or read from the backend. */
  optimistic?: boolean;
  pending_reconciliation?: boolean;
  optimistic_session_id?: string;
  optimistic_created_at?: string;
};

/**
 * How long an optimistic order may survive without backend confirmation.
 * After this window, an unreconciled optimistic order is dropped instead of
 * being retained forever (which could mask a readback failure such as #423).
 */
const OPTIMISTIC_RECONCILIATION_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Injectable clock for the reconciliation window. Production uses the real
 * Date.now(); tests replace ``now`` to exercise expiry deterministically
 * without real waits or arbitrary timers.
 */
export const reconciliationClock = {
  now: (): number => Date.now(),
};

/**
 * An order may only be retained across a readback when it is explicitly
 * optimistic, pending reconciliation, belongs to the current session, and has
 * not exceeded the reconciliation window. Everything else must follow the
 * backend's authoritative response.
 */
function isRetentionEligible(order: ClientOrder, sessionId: string): boolean {
  if (order.optimistic !== true || order.pending_reconciliation !== true) {
    return false;
  }
  const orderSessionId = String(order.optimistic_session_id || '').trim();
  if (!sessionId || !orderSessionId || orderSessionId !== sessionId) {
    return false;
  }
  const createdAt = order.optimistic_created_at
    ? Date.parse(order.optimistic_created_at)
    : Number.NaN;
  if (!Number.isFinite(createdAt)) {
    return false;
  }
  if (reconciliationClock.now() - createdAt > OPTIMISTIC_RECONCILIATION_TIMEOUT_MS) {
    return false;
  }
  return true;
}

function normalizeSessionSummary(raw: unknown): SessionSummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const summary = raw as Record<string, unknown>;
  return {
    status: String(summary.status || '').trim(),
    subtotal: Number(summary.subtotal || 0),
    tax_amount: Number(summary.tax_amount || 0),
    tip_amount: Number(summary.tip_amount || 0),
    total_amount: Number(summary.total_amount || 0),
  };
}

function normalizeOrder(raw: unknown): ClientOrder | null {
  if (!raw || typeof raw !== 'object') return null;
  const source = raw as Record<string, unknown>;
  const id = String(source.id || '').trim();
  if (!id) return null;

  return {
    id,
    display_id: String(source.display_id || source.display_order_id || '').trim() || undefined,
    display_order_id: String(source.display_order_id || '').trim() || undefined,
    workflow_status: String(source.workflow_status || '').trim() || undefined,
    status: String(source.status || '').trim() || undefined,
    subtotal: Number(source.subtotal || 0),
    tax_amount: Number(source.tax_amount || 0),
    tip_amount: Number(source.tip_amount || 0),
    total_amount: Number(source.total_amount || 0),
    items: Array.isArray(source.items) ? (source.items as OrderItem[]) : [],
    created_at: String(source.created_at || '').trim() || undefined,
    optimistic: source.optimistic === true,
    pending_reconciliation: source.pending_reconciliation === true,
    optimistic_session_id: String(source.optimistic_session_id || '').trim() || undefined,
    optimistic_created_at: String(source.optimistic_created_at || '').trim() || undefined,
  };
}

export const useOrdersStore = defineStore('client-orders', () => {
  const persistence = OrderPersistence.getInstance();
  const orders = ref<ClientOrder[]>(persistence.getOrders());
  const loading = ref(false);
  const error = ref<string | null>(null);

  const requestingCheck = ref(false);
  const showPaymentWaiting = ref(false);
  const paymentConfirmed = ref(false);
  const stripeEnabled = ref(true);
  const stripePublishableKey = ref<string>((window as any).STRIPE_PUBLISHABLE_KEY || '');
  const sessionSummary = ref<SessionSummary | null>(null);
  const cancellingOrderId = ref<string | null>(null);

  const pollingIntervalId = ref<number | null>(null);
  const pollingIntervalCount = ref(0);
  const pollingSessionId = ref<string | number | null>(null);

  const activeOrders = computed(() => {
    // Filter orders by terminal_statuses from config (backend as single source of truth)
    const config = (window as any).APP_CONFIG?.ui_config;
    if (!config?.constraints?.orders?.terminal_statuses) {
      // Fallback: if config not available, return all orders (legacy behavior)
      return orders.value;
    }
    
    const terminalStatuses = new Set(
      config.constraints.orders.terminal_statuses.map((s: string) => s.toLowerCase())
    );
    
    return orders.value.filter((order) => {
      const status = (order.workflow_status || order.status || '').toLowerCase();
      return !terminalStatuses.has(status);
    });
  });

  const activeOrdersCount = computed(() => activeOrders.value.length);
  const hasActiveOrders = computed(() => activeOrdersCount.value > 0);
  const isPolling = computed(() => pollingIntervalId.value !== null);

  const persistOrders = () => {
    const activeSessionId = String(useSessionStore().sessionId || '').trim();
    if (!activeSessionId) return;
    const filtered = orders.value.filter((o) =>
      o.optimistic === true &&
      o.pending_reconciliation === true &&
      isRetentionEligible(o, activeSessionId),
    );
    persistence.setOrders(filtered);
  };

  const persistOrdersAfterChange = () => {
    persistOrders();
    emitObservation('orders_persisted', {
      persisted_count: (orders.value as ClientOrder[]).filter(
        (o) => o.optimistic === true && o.pending_reconciliation === true
      ).length,
    });
  };

  const fetchActiveOrders = async (sessionId?: string | number): Promise<ClientOrder[]> => {
    loading.value = true;
    error.value = null;

    try {
      const response = await requestJSON<{ orders?: unknown[] }>('/api/customer/orders');
      const normalizedOrders = (response.orders || [])
        .map((candidate) => normalizeOrder(candidate))
        .filter((candidate): candidate is ClientOrder => candidate !== null);

      const remoteIds = new Set(normalizedOrders.map((o) => String(o.id)));
      const activeSessionId = String(sessionId || '').trim() || String(useSessionStore().sessionId || '').trim();
      // Only orders explicitly marked optimistic and pending reconciliation may
      // survive a readback that does not return them (current session, fresh
      // created_at). Anything else follows the backend's authoritative answer.
      const retainedActiveOrders = activeOrders.value.filter(
        (existing) =>
          !remoteIds.has(String(existing.id)) &&
          isRetentionEligible(existing, activeSessionId),
      );
      orders.value = [...normalizedOrders, ...retainedActiveOrders];

      if (activeSessionId) {
        try {
          const summaryPayload = await requestJSON<unknown>(
            `/api/customer/orders/session/${encodeURIComponent(activeSessionId)}`,
          );
          const rawSummary =
            (summaryPayload as any)?.session ||
            (summaryPayload as any)?.summary ||
            summaryPayload;
          sessionSummary.value = normalizeSessionSummary(rawSummary);
        } catch (summaryError) {
          log.warn('Failed to fetch session summary', summaryError);
        }
      }

      // Clear persisted state after a successful readback (orders reconciled with backend)
      const hasUnconfirmedOptimistic = orders.value.some(
        (o) => o.optimistic === true && o.pending_reconciliation === true
      );
      if (!hasUnconfirmedOptimistic) {
        persistence.clearOrders();
      } else {
        persistOrders();
      }

      return normalizedOrders;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al cargar órdenes';
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getOrderById = (orderId: string): ClientOrder | undefined => {
    return orders.value.find((order) => String(order.id) === String(orderId));
  };

  const addOrder = (order: ClientOrder): void => {
    const normalized = normalizeOrder(order);
    if (!normalized) return;
    const existing = orders.value.find((candidate) => candidate.id === normalized.id);
    if (existing) {
      if (existing.items.length > 0 && normalized.items.length === 0) {
        normalized.items = existing.items;
      }
      if (normalized.total_amount === 0 && existing.total_amount > 0) {
        normalized.total_amount = existing.total_amount;
      }
      // A skeleton update must not strip the reconciliation metadata of the
      // optimistic order registered by submitCart.
      if (existing.optimistic && !normalized.optimistic) {
        normalized.optimistic = true;
        normalized.pending_reconciliation =
          existing.pending_reconciliation || true;
        normalized.optimistic_session_id = existing.optimistic_session_id;
        normalized.optimistic_created_at = existing.optimistic_created_at;
      }
    }
    if (normalized.optimistic) {
      // Controlled reconciliation metadata: the store owns these fields.
      if (!normalized.pending_reconciliation) {
        normalized.pending_reconciliation = true;
      }
      if (!normalized.optimistic_session_id) {
        const sessionId = String(useSessionStore().sessionId || '').trim();
        normalized.optimistic_session_id = sessionId || undefined;
      }
      if (!normalized.optimistic_created_at) {
        normalized.optimistic_created_at = new Date().toISOString();
      }
    }
    orders.value = [normalized, ...orders.value.filter((candidate) => candidate.id !== normalized.id)];
    persistOrdersAfterChange();
    // #471: Trigger immediate readback so the order appears in activeOrders
    // without waiting for the polling interval (prevents "Estado" button staying disabled).
    if (normalized.optimistic === true) {
      void fetchActiveOrders();
    }
  };

  const removeOrder = (orderId: string): void => {
    orders.value = orders.value.filter((order) => String(order.id) !== String(orderId));
    persistOrdersAfterChange();
  };

  const clearOrders = (): void => {
    orders.value = [];
    sessionSummary.value = null;
    persistence.clearOrders();
  };

  /**
   * Cancel an active order via the backend endpoint.
   * POST /api/customer/orders/:orderId/cancel
   *
   * Guards against double-click: if the same orderId is already cancelling,
   * returns false without sending a second request.
   *
   * @param orderId - The order to cancel
   * @param reason - Optional cancellation reason
   * @returns true if successful, false otherwise
   */
  const cancelOrder = async (orderId: string, reason?: string): Promise<boolean> => {
    error.value = null;

    const resolvedId = String(orderId || '').trim();
    if (!resolvedId) {
      error.value = 'ID de orden no proporcionado';
      return false;
    }

    // Guard against double-click / concurrent requests
    if (cancellingOrderId.value === resolvedId) {
      return false;
    }

    cancellingOrderId.value = resolvedId;

    try {
      await requestJSON(`/api/customer/orders/${encodeURIComponent(resolvedId)}/cancel`, {
        method: 'POST',
        body: reason ? { reason } : {},
      });
      // Refresh to ensure consistency (order moves to terminal)
      await fetchActiveOrders();
      removeOrder(resolvedId);
      persistOrdersAfterChange();
      return true;
    } catch (cancelError) {
      error.value = cancelError instanceof Error ? cancelError.message : 'Error al cancelar orden';
      log.error('Failed to cancel order', cancelError);
      return false;
    } finally {
      cancellingOrderId.value = null;
    }
  };

  const requestCheck = async (sessionId?: string | number): Promise<boolean> => {
    const sessionStore = useSessionStore();
    const resolvedSessionId = String(sessionId || sessionStore.sessionId || '').trim();

    if (!resolvedSessionId) {
      error.value = 'No hay sesión activa para solicitar la cuenta';
      return false;
    }

    requestingCheck.value = true;
    try {
      await requestJSON(
        `/api/customer/orders/session/${encodeURIComponent(resolvedSessionId)}/request-check`,
        {
          method: 'POST',
        },
      );

      showPaymentWaiting.value = true;
      return true;
    } catch (requestError) {
      log.error('Failed to request check', requestError);
      return false;
    } finally {
      requestingCheck.value = false;
    }
  };

  const stopPolling = (): void => {
    if (pollingIntervalId.value !== null) {
      window.clearInterval(pollingIntervalId.value);
      pollingIntervalId.value = null;
    }
    pollingIntervalCount.value = pollingIntervalId.value === null ? 0 : 1;

    emitObservation('polling_stopped', {
      polling_interval_count: pollingIntervalCount.value,
      session_id: pollingSessionId.value ? String(pollingSessionId.value) : null,
    });

    pollingSessionId.value = null;
  };

  const startPolling = (sessionId?: string | number): void => {
    const resolvedSessionId = String(sessionId || useSessionStore().sessionId || '').trim() || null;

    if (pollingIntervalId.value !== null && pollingSessionId.value === resolvedSessionId) {
      emitObservation('polling_started', {
        polling_interval_count: pollingIntervalCount.value,
        session_id: resolvedSessionId,
      });
      return;
    }

    if (pollingIntervalId.value !== null) {
      stopPolling();
    }

    pollingSessionId.value = resolvedSessionId;
    void fetchActiveOrders(resolvedSessionId || undefined);
    pollingIntervalId.value = window.setInterval(() => {
      void fetchActiveOrders(resolvedSessionId || undefined);
    }, 8000);
    pollingIntervalCount.value = pollingIntervalId.value === null ? 0 : 1;

    emitObservation('polling_started', {
      polling_interval_count: pollingIntervalCount.value,
      session_id: resolvedSessionId,
    });
  };

  return {
    orders,
    loading,
    error,
    requestingCheck,
    showPaymentWaiting,
    paymentConfirmed,
    stripeEnabled,
    stripePublishableKey,
    sessionSummary,
    activeOrders,
    activeOrdersCount,
    hasActiveOrders,
    isPolling,
    pollingIntervalCount,
    fetchActiveOrders,
    cancelOrder,
    cancellingOrderId,
    getOrderById,
    addOrder,
    removeOrder,
    clearOrders,
    requestCheck,
    startPolling,
    stopPolling,
  };
});
